/**
 * AuthContext — gère l'authentification globale.
 * Fonctionne avec ou sans backend (fallback mock).
 */
import { createContext, useState, useEffect, useCallback } from "react";
import { authApi, tokenStorage } from "../services/api";
import { mockLogin } from "../services/mockData";

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => tokenStorage.getUser());
  const [loading, setLoading] = useState(true);
  const [usingMock, setUsingMock] = useState(false);

  /** Au montage, tente de récupérer l'utilisateur depuis l'API si token présent. */
  useEffect(() => {
    const init = async () => {
      const token = tokenStorage.getAccess();
      if (!token) {
        setLoading(false);
        return;
      }
      // Si token mock, on garde la session locale
      if (token.startsWith("mock-")) {
        setUsingMock(true);
        setLoading(false);
        return;
      }
      try {
        const { data } = await authApi.me();
        setUser(data);
        tokenStorage.set({ user: data });
      } catch {
        tokenStorage.clear();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  /** Connexion (essaie API, fallback mock en cas d'échec réseau). */
  const login = useCallback(async (email, password) => {
    try {
      const { data } = await authApi.login(email, password);
      tokenStorage.set({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        user: data.user,
      });
      setUser(data.user);
      setUsingMock(false);
      return { ok: true, user: data.user };
    } catch (err) {
      // Si erreur réseau (backend down), tentons le mock
      const isNetworkError = !err.response || err.code === "ERR_NETWORK";
      if (isNetworkError) {
        const mock = mockLogin(email, password);
        if (mock) {
          tokenStorage.set({
            access_token: mock.access_token,
            refresh_token: mock.refresh_token,
            user: mock.user,
          });
          setUser(mock.user);
          setUsingMock(true);
          return { ok: true, user: mock.user, mock: true };
        }
        return {
          ok: false,
          error: "Backend indisponible. Identifiants démo : admin@monministere.com / admin123",
        };
      }
      return {
        ok: false,
        error: err.response?.data?.detail || "Erreur de connexion",
      };
    }
  }, []);

  /** Met à jour les données du user en mémoire + localStorage (sans re-fetch).
   *  Les composants qui lisent `user` (Sidebar, header...) se re-rendent automatiquement.
   */
  const updateUser = useCallback((data) => {
    if (!data) return;
    setUser((prev) => {
      const next = prev ? { ...prev, ...data } : data;
      try {
        tokenStorage.set({ user: next });
      } catch { /* ignore quota errors */ }
      return next;
    });
  }, []);

  /** Déconnexion locale + appel API best-effort. */
  const logout = useCallback(async () => {
    try {
      if (!usingMock) await authApi.logout();
    } catch {
      /* on ignore les erreurs côté API */
    } finally {
      tokenStorage.clear();
      setUser(null);
      setUsingMock(false);
    }
  }, [usingMock]);

  const value = {
    user,
    loading,
    usingMock,
    isAuthenticated: !!user,
    isAdmin: user?.role === "pasteur" || user?.role === "secretaire" || user?.role === "admin",
    login,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
